import { format, isValid, parseISO } from 'date-fns'

import { EntradasMapper } from '@/domain/simulador/entradas-mapper'
import type { TSimulacaoCalculoRotinaArquivo } from '@/domain/simulador/factories/types/simulacao-calculo'
import type { IParcela } from '@/domain/simulador/gerenciador-pos-conclusao.interface'
import { IntermediariasMapper } from '@/domain/simulador/intermediarias-mapper'
import { MensaisMapper } from '@/domain/simulador/mensais-mapper'
import { CalculadoraDeDescontoExtra } from '@/features/simulador/brasil/domain/calculadora-de-desconto-extra'
import { CalculadoraDeFatorDeVendedor } from '@/features/simulador/brasil/domain/calculadora-de-fator-vendedor'
import { Arquivo } from '@/features/simulador/shared/domain/arquivo'
import { CalculadoraDeComissao } from '@/features/simulador/shared/domain/calculadora-de-comissao'
import { CalculadoraDeFator } from '@/features/simulador/shared/domain/calculadora-de-fator'
import { CalculadoraDeMargem } from '@/features/simulador/shared/domain/calculadora-de-margem'
import { CalculadoraDeParcelasBase } from '@/features/simulador/shared/domain/calculadora-de-parcelas'
import { CalculadoraDeValorMedio } from '@/features/simulador/shared/domain/calculadora-de-valor-medio'
import { CalculadoraDeValorPresenteBase } from '@/features/simulador/shared/domain/calculadora-de-valor-presente'
import { ConversorJuros } from '@/features/simulador/shared/domain/conversor-juros'
import { Mascara } from '@/features/simulador/shared/domain/mascara'
import { PrazoPagamentoSimulacao } from '@/features/simulador/shared/domain/prazo-pagamento-simulacao'
import { Rotina } from '@/features/simulador/shared/domain/rotina'
import type { TParametrosSimulacao } from '@/types/parametros-simulacao'
import type { TCondicoesDePrecoProduto, TTabelaDePrecoProduto } from '@/types/simulacao'
import { CalculadoraDeValoresTotaisBase } from '@/utils/calculadora-valores-totais'
import { constantsSimulacao } from '@/utils/constants-simulacao'
import { maxDate } from '@/utils/date.utils'
import { resolvePathWithQuery } from '@/utils/object.utils'

export type TParametrosRotinaArquivo = Pick<
  TCondicoesDePrecoProduto,
  | 'percentualJurosAnualAntesPontoMedio'
  | 'percentualJurosAnualDepoisPontoMedio'
  | 'percentualComissaoVendedor'
  | 'percentualISS'
  | 'percentualImpostos'
  | 'percentualIRPJ'
  | 'percentualINCC'
  | 'percentualAutonomiaVendedorSobrePoupanca1Por3'
  | 'percentualAutonomiaVendedorSobrePoupanca2Por3'
  | 'dataPontoMedio'
  | 'patrimonioAfetacao'
  | 'modoDeLucroDaEmpresa'
  | 'dataSaldo'
  | 'taxaDeDescontoExtra'
  | 'percentualDePoupancaMinimaParaDescontoExtra'
  | 'percentualDePoupancaMinimaNaMetadeDoPrazoDePagamento'
> &
  Pick<
    TTabelaDePrecoProduto,
    | 'margemVendedor1Por3'
    | 'margemVendedor2Por3'
    | 'margemVendedor3Por3'
    | 'margemGcom'
    | 'margemGR'
    | 'custoEmUPF'
    | 'valorAVistaGr'
    | 'valorAVistaGcom'
    | 'valorAVistaVendedor'
  > &
  Pick<
    TParametrosSimulacao,
    | 'mascaraVendedor'
    | 'mascaraGcom'
    | 'mascaraGR'
    | 'ordemMascaraVendedor'
    | 'ordemMascaraGcom'
    | 'ordemMascaraGR'
    | 'taxaUpf'
  >

export class RotinaArquivoBase {
  private readonly calculadoraComissao: CalculadoraDeComissao
  private readonly VALOR_MINIMO_CALCULAR = 0.01

  constructor(
    readonly parametros: TParametrosRotinaArquivo,
    private readonly calculadoraValorPresente: CalculadoraDeValorPresenteBase = new CalculadoraDeValorPresenteBase(),
    private readonly calculadoraParcelas: CalculadoraDeParcelasBase = new CalculadoraDeParcelasBase(),
    private readonly calculadoraDeMargem: CalculadoraDeMargem = new CalculadoraDeMargem(),
    private readonly calculadoraDeFator: CalculadoraDeFator = new CalculadoraDeFator(),
    private readonly mascara: Mascara = new Mascara(),
  ) {
    this.calculadoraComissao = new CalculadoraDeComissao(this.parametros.percentualComissaoVendedor)
  }

  public calcular(grupos: TSimulacaoCalculoRotinaArquivo) {
    const dataSimulacao = grupos.dataSimulacao
    const entradas = EntradasMapper.transform(grupos.entradas ?? [])
    const mensais = MensaisMapper.transform(grupos.mensais ?? [])
    const intermediarias = IntermediariasMapper.transform(grupos.intermediarias ?? [])
    const posConclusao = MensaisMapper.transform(grupos.posConclusao ? [grupos.posConclusao] : [])

    const gruposParcelas = { entradas, mensais, intermediarias, posConclusao }

    const tiposParcelas = ['entradas', 'mensais', 'intermediarias', 'posConclusao'] as const
    let parcelas: IParcela[] = tiposParcelas.flatMap((tipo) =>
      gruposParcelas[tipo].flatMap((grupo) => grupo.parcelas.map((parcela) => ({ ...parcela, tipo }))),
    )

    if (grupos.saldo?.data && grupos.saldo?.valor) {
      parcelas.push({ tipo: 'saldo', data: grupos.saldo.data, valor: grupos.saldo.valor })
    }

    const isSimulacaoSemValorPreenchido = [
      !grupos.entradas?.length,
      !grupos.mensais?.length,
      !grupos.intermediarias?.length,
      !grupos.saldo?.valor,
    ].every(Boolean)

    if (isSimulacaoSemValorPreenchido) {
      parcelas = parcelas.map((parcela) =>
        parcela.tipo === 'saldo' ? { ...parcela, valor: this.VALOR_MINIMO_CALCULAR } : parcela,
      )
    }

    const descontoExtra = CalculadoraDeDescontoExtra.habilitar(this.parametros)?.calcular(
      parcelas,
      dataSimulacao,
      grupos.saldo?.data,
    )

    const taxaJurosDiarioAntesPontoMedio = ConversorJuros.converterTaxaDeJurosAnualParaDiaria(
      this.parametros.percentualJurosAnualAntesPontoMedio,
    )
    const taxaJurosDiarioDepoisPontoMedio = ConversorJuros.converterTaxaDeJurosAnualParaDiaria(
      this.parametros.percentualJurosAnualDepoisPontoMedio,
    )
    const taxaJurosDiariaINCC = ConversorJuros.converterTaxaDeJurosAnualParaDiaria(this.parametros.percentualINCC ?? 0)

    parcelas = this.calculadoraValorPresente.calcularValorPresenteDasParcelas(
      parcelas,
      dataSimulacao,
      taxaJurosDiarioDepoisPontoMedio,
    )

    const datasValidas = parcelas.map((parcela) => parseISO(parcela.data)).filter(isValid)
    const dataUltimaParcelaComPosConclusao =
      datasValidas.length > 0 ? format(maxDate(datasValidas), 'yyyy-MM-dd') : dataSimulacao
    const { prazoPagamentoEmMeses } = new PrazoPagamentoSimulacao(dataSimulacao, dataUltimaParcelaComPosConclusao)
    const isPrazoPagamentoEmMesesMenorQueLimiteMinimo =
      prazoPagamentoEmMeses <= constantsSimulacao.LIMITE_MINIMO_NOVA_FORMA_PAGAMENTO
    if (isPrazoPagamentoEmMesesMenorQueLimiteMinimo) {
      parcelas = this.calculadoraParcelas.calcularValorSemCorrecaoDasParcelas(
        parcelas,
        dataSimulacao,
        taxaJurosDiariaINCC,
      )
    }

    const valorPresente = this.calculadoraParcelas.somar(parcelas, 'valorPresente')
    const comissaoVendedor = this.calculadoraComissao.calcularComissaoVendedor(valorPresente)
    const valorParcelaBase = isPrazoPagamentoEmMesesMenorQueLimiteMinimo ? 'valorSemCorrecao' : 'valor'

    parcelas = this.calculadoraParcelas.calcularValorDeImpostoDasParcelas(
      parcelas,
      this.parametros.percentualImpostos,
      this.parametros.percentualISS,
      valorParcelaBase,
    )
    parcelas = this.calculadoraParcelas.calcularValorSemImpostoEComissaoDasParcelas(
      parcelas,
      comissaoVendedor,
      valorParcelaBase,
    )
    parcelas = this.calculadoraParcelas.calcularValorDePontoMedioDasParcelas(
      parcelas,
      taxaJurosDiarioAntesPontoMedio,
      taxaJurosDiarioDepoisPontoMedio,
      this.parametros.dataPontoMedio,
    )

    const valorMedioTotal = this.calculadoraParcelas.somar(parcelas, 'valorPontoMedio')

    const valorMedioTotalUPF = CalculadoraDeValorMedio.calcularEmUPF(valorMedioTotal, this.parametros.taxaUpf)

    const margemBrutaEmUPF = this.calculadoraDeMargem.calcularMargemBrutaEmUPF(
      valorMedioTotalUPF,
      this.parametros.custoEmUPF,
    )

    const margemLiquidaEmUPF = this.calculadoraDeMargem.calcularMargemLiquidaEmUPF(
      margemBrutaEmUPF,
      this.parametros.patrimonioAfetacao,
      this.parametros.modoDeLucroDaEmpresa,
      this.parametros.percentualIRPJ,
    )

    const margemRealizada = this.calculadoraDeMargem.calcularMargemRealizada(margemLiquidaEmUPF, {
      margemGR: this.parametros.margemGR,
      margemGcom: this.parametros.margemGcom,
      margemVendedor1Por3: this.parametros.margemVendedor1Por3,
      margemVendedor2Por3: this.parametros.margemVendedor2Por3,
      margemVendedor3Por3: this.parametros.margemVendedor3Por3,
    })

    const margens = this.calculadoraDeFator.calcularFatorAlcancado(margemRealizada)

    const calculadoraDeValoresTotais = new CalculadoraDeValoresTotaisBase(grupos)
    const { valorSaldo: totalSaldo, valorPoupanca } = calculadoraDeValoresTotais

    const porcentagemPoupanca = (valorPoupanca * 100) / (valorPoupanca + totalSaldo)

    const fatorVendedor = CalculadoraDeFatorDeVendedor.calcular(margens, porcentagemPoupanca, this.parametros) ?? 0

    const fatorVendedor1Por3 = resolvePathWithQuery(margens, '[tipo=margemVendedor1Por3].fator') ?? 0
    const fatorVendedor2Por3 = resolvePathWithQuery(margens, '[tipo=margemVendedor2Por3].fator') ?? 0
    const fatorVendedor3Por3 = resolvePathWithQuery(margens, '[tipo=margemVendedor3Por3].fator') ?? 0
    const fatorGcom = resolvePathWithQuery(margens, '[tipo=margemGcom].fator') ?? 0
    const fatorGR = resolvePathWithQuery(margens, '[tipo=margemGR].fator') ?? 0

    const { mascaraGcom, mascaraGR, mascaraVendedor } = this.mascara.aplicarMascaraNosFatores(
      descontoExtra?.fatores ?? { fatorVendedor, fatorGcom, fatorGR },
      this.parametros,
    )

    const valorPresentePontoMedio = this.calculadoraValorPresente.calcularValorPresenteNoPontoMedio({
      valorPresente,
      taxaJurosDiarioDepoisPontoMedio,
      dataSimulacao,
      dataPontoMedio: this.parametros.dataPontoMedio,
    })
    const valorPresentePontoMedioUPF = this.calculadoraValorPresente.calcularValorPresenteNoPontoMedioEmUPF(
      valorPresentePontoMedio,
      this.parametros.taxaUpf,
    )
    const percentualMargemVenda = this.calculadoraDeMargem.calcularPorcentagemMargemDeVenda(
      margemLiquidaEmUPF,
      valorPresentePontoMedioUPF,
    )

    const numeroDeRotina = Rotina.montar({
      ordemMascaraVendedor: this.parametros.ordemMascaraVendedor,
      mascaraVendedor,
      percentualMargemVenda,
    })

    const arquivo = Arquivo.montar(this.mascara, {
      valorPresente: descontoExtra?.valorPresente ?? valorPresente,
      ordemMascaraGcom: this.parametros.ordemMascaraGcom,
      ordemMascaraGR: this.parametros.ordemMascaraGR,
      mascaraGcom,
      mascaraGR,
    })

    return {
      valorPresente: Number(valorPresente.toFixed(2)),
      comissaoVendedor: Number(comissaoVendedor.toFixed(2)),
      valorMedioTotal: Number(valorMedioTotal.toFixed(2)),
      valorMedioTotalUPF: Number(valorMedioTotalUPF.toFixed(2)),
      margemBruta: Number(margemBrutaEmUPF.toFixed(2)),
      margemLiquida: Number(margemLiquidaEmUPF.toFixed(2)),
      fatorVendedor1Por3: Number(fatorVendedor1Por3.toFixed(3)),
      fatorVendedor2Por3: Number(fatorVendedor2Por3.toFixed(3)),
      fatorVendedor3Por3: Number(fatorVendedor3Por3.toFixed(3)),
      fatorGcom: Number(fatorGcom.toFixed(3)),
      fatorGR: Number(fatorGR.toFixed(3)),
      valorPresentePontoMedio: Number(valorPresentePontoMedio.toFixed(2)),
      valorPresentePontoMedioUPF: Number(valorPresentePontoMedioUPF.toFixed(2)),
      percentualMargemVenda: Number(percentualMargemVenda.toFixed(2)),
      rotina: numeroDeRotina,
      arquivo,
    }
  }
}
